import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import { api } from '../lib/api';
import type { CurrentUser } from '../types';

interface AuthContextValue {
    user: CurrentUser | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    isAdmin: boolean;
    error: string | null;
    login: () => Promise<void>;
    logout: () => void;
    refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Session du Hub.
 *
 * Contrairement au CRA, aucun jeton applicatif n'est émis : le jeton d'accès
 * Entra ID est présenté directement à l'API, qui en vérifie la signature. Le
 * profil renvoyé par `/api/auth/me` ne fait que refléter ce que la base sait de
 * l'utilisateur ; il ne sert jamais à décider d'un droit côté navigateur.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
    const { instance, accounts, inProgress } = useMsal();
    const account = useAccount(accounts[0] ?? null);

    const [user, setUser] = useState<CurrentUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadProfile = async () => {
        try {
            setError(null);
            const profile = await api.get<CurrentUser>('/api/auth/me');
            setUser(profile);
        } catch (err) {
            setUser(null);
            setError(
                err instanceof Error
                    ? err.message
                    : 'Impossible de récupérer votre profil.'
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        api.configure(instance, account ?? null);

        if (inProgress !== 'none') return;

        if (!account) {
            setUser(null);
            setIsLoading(false);
            return;
        }

        void loadProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account, inProgress, instance]);

    const login = async () => {
        try {
            setError(null);
            await instance.loginRedirect(loginRequest);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : 'La connexion a échoué.'
            );
        }
    };

    const logout = () => {
        void instance.logoutRedirect();
        setUser(null);
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                isLoading: isLoading || inProgress !== 'none',
                isAuthenticated: Boolean(account && user),
                isAdmin: user?.role === 'admin',
                error,
                login,
                logout,
                refresh: loadProfile,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth doit être utilisé à l’intérieur de AuthProvider');
    }
    return context;
}
